import { useMemo, type CSSProperties } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { customersApi } from '@/api/customers';
import { extractApiError } from '@/api/client';
import { customerDisplayLabel, isCustomerUnnamed } from '@/lib/placeholders';

// Plan v0.4.0 §N-1 (v0.3.0 §G-2 follow-up) — admin inbox for
// half-captured rows. Lists Customers whose `displayName` /
// `legalName` is empty (the §G-2 backend allows blank capture
// for soft-recording leads). Per-row link goes to the
// CustomerMasterPage with the row pre-selected so the admin
// lands on the detail panel ready to edit.
//
// v1 scope: Customers only. Robots / Stores incomplete records
// follow in subsequent PRs once their field-level "blank"
// definitions are agreed (e.g. is a robot without serialShort
// "incomplete" if it has serialLong?).

const PAGE_SIZE = 200; // pull a wide window so admin can sweep in one pass

export function IncompleteRecordsPage() {
  const customersQ = useQuery({
    queryKey: ['admin', 'incomplete-records', 'customers'],
    queryFn: () => customersApi.list({ pageSize: PAGE_SIZE }),
    staleTime: 30_000,
  });

  // Surface only rows that match isCustomerUnnamed (no
  // displayName) OR have a blank legalName. The page can grow
  // additional predicates as more "incomplete" shapes get
  // agreed by ops.
  const incompleteCustomers = useMemo(() => {
    const items = customersQ.data?.items ?? [];
    return items.filter((c) => {
      const noDisplay = isCustomerUnnamed(c);
      const noLegal = !c.legalName || c.legalName.trim().length === 0;
      return noDisplay || noLegal;
    });
  }, [customersQ.data]);

  // 추가수정 2차(26-07-27) §1 — 이 점검은 한 번에 PAGE_SIZE(=서버 상한 200)곳만
  // 훑는다. 고객사가 그보다 많으면 뒷부분은 아예 스캔되지 않아 "누락 행이 없다"가
  // 사실이 아닐 수 있다. 근본 수정(전량 스캔/페이징)은 후속(부록 A-6)이고, 이번엔
  // 잘렸다는 사실만 알린다 — 고객사 서류 화면의 기존 잘림 안내와 같은 패턴.
  const scannedCount = customersQ.data?.items.length ?? 0;
  const scanTruncated = (customersQ.data?.total ?? 0) > scannedCount;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <header>
        <h1 className="kuun-h2" style={{ margin: 0 }}>
          미입력 인박스
        </h1>
      </header>

      <section style={card}>
        <div style={cardHeader}>
          <strong style={{ fontSize: 13 }}>고객사 ({incompleteCustomers.length}건)</strong>
          <span className="kuun-caption" style={{ color: 'var(--fg-muted)' }}>
            표시명이나 법인명이 비어 있는 곳
          </span>
        </div>

        {customersQ.error && <div style={errorBox}>{extractApiError(customersQ.error)}</div>}

        {scanTruncated && (
          <div style={{ marginBottom: 6, fontSize: 11, color: 'var(--fg-muted)' }}>
            전체 {(customersQ.data?.total ?? 0).toLocaleString()}곳 중 {scannedCount}곳만
            점검했습니다.
          </div>
        )}

        <table style={tableBase}>
          <thead>
            <tr>
              <th style={th}>표시명</th>
              <th style={th}>법인명</th>
              <th style={th}>세그먼트</th>
              <th style={th}>법인</th>
              <th style={{ ...th, textAlign: 'right' }}>액션</th>
            </tr>
          </thead>
          <tbody>
            {incompleteCustomers.map((c) => {
              const noDisplay = isCustomerUnnamed(c);
              const noLegal = !c.legalName || c.legalName.trim().length === 0;
              return (
                <tr key={c.id} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                  <td
                    style={{
                      ...td,
                      fontStyle: noDisplay ? 'italic' : 'normal',
                      color: noDisplay ? 'var(--fg-muted)' : undefined,
                    }}
                  >
                    {customerDisplayLabel(c)}
                  </td>
                  <td
                    style={{
                      ...td,
                      fontStyle: noLegal ? 'italic' : 'normal',
                      color: noLegal ? 'var(--fg-muted)' : undefined,
                    }}
                  >
                    {c.legalName || '(미입력)'}
                  </td>
                  <td style={td}>{c.segment}</td>
                  <td style={td}>{c.ownerEntity}</td>
                  <td style={{ ...td, textAlign: 'right' }}>
                    <Link to={`/customers/${c.id}`} style={editLink}>
                      수정 →
                    </Link>
                  </td>
                </tr>
              );
            })}
            {!customersQ.isLoading && incompleteCustomers.length === 0 && (
              <tr>
                <td colSpan={5} style={empty}>
                  빠진 곳이 없습니다. 모든 고객사에 표시명과 법인명이 들어 있습니다.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </section>

      <section style={card}>
        <div style={cardHeader}>
          <strong style={{ fontSize: 13 }}>로봇 / 매장</strong>
          <span
            style={{
              fontSize: 10,
              padding: '2px 8px',
              borderRadius: 'var(--r-pill)',
              background: 'var(--bg-warning-subtle)',
              color: 'var(--fg-on-warning-tint)',
              fontWeight: 500,
            }}
          >
            v0.4.0 후속 예정
          </span>
        </div>
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--fg-muted)' }}>
          <div style={{ marginTop: 8, fontSize: 13 }}>
            로봇과 매장은 어떤 항목이 빠진 것인지 기준이 정해지면 여기에 함께 보여 드립니다.
          </div>
        </div>
      </section>
    </div>
  );
}

// ============================================================
// Styles
// ============================================================

const card: CSSProperties = {
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-card)',
  background: 'var(--bg-surface)',
  padding: 16,
};

const cardHeader: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: 8,
  gap: 8,
};

const tableBase: CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
};

const th: CSSProperties = {
  textAlign: 'left',
  fontSize: 11,
  fontWeight: 600,
  color: 'var(--fg-muted)',
  padding: '8px 10px',
  borderBottom: '1px solid var(--border-subtle)',
};

const td: CSSProperties = {
  fontSize: 13,
  padding: '8px 10px',
  verticalAlign: 'middle',
};

const empty: CSSProperties = {
  padding: 24,
  textAlign: 'center',
  fontSize: 13,
  color: 'var(--fg-muted)',
};

const editLink: CSSProperties = {
  color: 'var(--fg-brand)',
  textDecoration: 'none',
  fontSize: 12,
};

const errorBox: CSSProperties = {
  padding: 10,
  background: 'var(--bg-danger-subtle)',
  border: '1px solid var(--fg-danger)',
  borderRadius: 'var(--r-card)',
  color: 'var(--fg-danger)',
  fontSize: 12,
  marginBottom: 8,
};
